import {MIN_CANVAS_WIDTH, MAX_CANVAS_WIDTH} from './constants';
import {
  Breakpoint,
  getMostSpecificBreakpoint,
  MediaFeature,
} from '@packages/systems/style/media-queries';
import {BREAKPOINT_ID_MAIN} from '@packages/systems/style/breakpoint-ids';
import type {BreakpointID, BreakpointsMap} from '@packages/systems/style/types';
import {weakMemo} from '@packages/utilities/memo';

const getBreakpointExpressionValues = (
  breakpoints: BreakpointsMap<Breakpoint>,
  filterFn: (arg1: MediaFeature) => boolean
) => {
  const values: Array<any | number> = [];
  // @ts-expect-error - TS2345 - Argument of type '(id: BreakpointID) => void' is not assignable to parameter of type '(value: string, index: number, array: string[]) => void'.
  Object.keys(breakpoints).forEach((id: BreakpointID) => {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    const expression = breakpoints[id].queries[0].expression;
    if (filterFn(expression)) {
      values.push(expression.value);
    }
  });
  return values;
};

export const getBreakpointVisualRange = (
  breakpoints: BreakpointsMap<Breakpoint>,
  currentBreakpoint: Breakpoint
): {
  min: number;
  max: number;
} => {
  if (!currentBreakpoint) return {min: MIN_CANVAS_WIDTH, max: MAX_CANVAS_WIDTH};

  const currentBreakpointMediaExpression =
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    currentBreakpoint.queries[0].expression;

  const result = {
    min: currentBreakpointMediaExpression.value,
    max: currentBreakpointMediaExpression.value,
  } as const;

  if (currentBreakpointMediaExpression.feature === 'maxWidth') {
    // @ts-expect-error - TS2540 - Cannot assign to 'min' because it is a read-only property.
    result.min =
      Math.max(
        -1,
        ...getBreakpointExpressionValues(
          breakpoints,
          (expr) =>
            expr.feature === 'maxWidth' &&
            expr.value < currentBreakpointMediaExpression.value
        )
      ) + 1;
  }

  if (
    currentBreakpoint.id === BREAKPOINT_ID_MAIN ||
    currentBreakpointMediaExpression.feature === 'minWidth'
  ) {
    // @ts-expect-error - TS2540 - Cannot assign to 'max' because it is a read-only property.
    result.max =
      Math.min(
        MAX_CANVAS_WIDTH + 1,
        ...getBreakpointExpressionValues(
          breakpoints,
          (expr) =>
            expr.feature === 'minWidth' &&
            (currentBreakpointMediaExpression.value < expr.value ||
              currentBreakpoint.id === BREAKPOINT_ID_MAIN)
        )
      ) - 1;
  }

  return result;
};

/*
 * Return ids of all breakpoints in order, in which they are the most specific if canvas width decrease from max to min.
 */
export const getBreakpointsVisualOrder = weakMemo(
  function getBreakpointsVisualOrder(
    breakpoints: BreakpointsMap<Breakpoint>
  ): BreakpointID[] {
    const visualBreakpointOrder: Array<BreakpointID> = [];

    for (let width = MAX_CANVAS_WIDTH; width >= MIN_CANVAS_WIDTH; width--) {
      const mostSpecificBreakpoint = getMostSpecificBreakpoint(breakpoints, {
        width,
      });

      if (mostSpecificBreakpoint) {
        const lastId = visualBreakpointOrder[visualBreakpointOrder.length - 1];

        if (lastId !== mostSpecificBreakpoint.id) {
          visualBreakpointOrder.push(mostSpecificBreakpoint.id);
        }
      }
    }

    return visualBreakpointOrder;
  }
);

type BreakpointKeyboardShortcutMap = {[key in BreakpointID]?: string};

/**
 * Given a list of `BreakpointID`s, generates a mapping of id to keyboard shortcut
 *
 * @param breakpoints
 * @returns Object where the key is a `BreakpointID` and the value is the label of the keyboard
 * shotcut that will activate the breakpoint
 */
export const createBreakpointsKeyboardShortcutMap = (
  breakpoints: BreakpointID[]
): BreakpointKeyboardShortcutMap => {
  return breakpoints.reduce<BreakpointKeyboardShortcutMap>(
    (res, currBreakpointId, idx) => {
      const keyboardShortcutLabel = `${idx + 1}`;
      res[currBreakpointId] = keyboardShortcutLabel;
      return res;
    },
    {}
  );
};
